import React, { useEffect, useRef, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { errorText } from '../../pages/SoftwarePolicy/policyEdit';
import { searchUrl } from '../../pages/SoftwareBuilder/builderLogic';
import { installedProgram } from './softwareBladeLogic';

const list = (v) => (Array.isArray(v) ? v : []);
export const INSTALL_URL = 'cma/softwareinstall/queue';
export const REMOVE_URL = 'cma/softwareinstall/remove';

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

// The blade on the right of the device page: search all the software we can install and put an install in the queue of the machine.
// `devices` is the chosen machines [{deviceId, hostname}]; `installed` is the programs the (one) machine has, so a result can say it is already
// there. It says nothing about the policy: that is on the device's own list.
//
// An install is made after a confirmation naming the machines. The page sends the
// program and the machines, never a script: the API decides what is run.
export default function SoftwareBlade({ account, devices, installed, onClose }) {
  const jApi = useApi();
  const apiRef = useRef(jApi);
  apiRef.current = jApi;
  const [text, setText] = useState('');
  const [found, setFound] = useState({ state: 'idle', packages: [] });
  const [confirming, setConfirming] = useState(null);       // { id, name } waiting for the go-ahead
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState(null);

  useEffect(() => {
    const q = text.trim();
    if (q.length < 2) { setFound({ state: 'idle', packages: [] }); return undefined; }
    let cancelled = false;
    setFound((f) => ({ ...f, state: 'loading' }));
    const timer = setTimeout(() => {
      apiRef.current.request('GET', searchUrl(q))
        .then((r) => { if (!cancelled) setFound({ state: 'ok', packages: list(r.data?.packages) }); })
        .catch((ex) => { if (!cancelled) setFound({ state: 'error', packages: [], error: errorText(ex) }); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [text]);

  const packages = found.packages;
  const total = devices.length;
  const names = devices.map((d) => d.hostname).join(', ');

  const run = async (item) => {
    setBusy(true);
    setOutcome(null);
    try {
      const response = await apiRef.current.request('POST', INSTALL_URL, { account, wingetId: item.id, kind: 'apply', deviceIds: devices.map((d) => d.deviceId) });
      setOutcome({ name: item.name, ...response.data });
    } catch (ex) {
      setOutcome({ name: item.name, error: errorText(ex) });
    } finally {
      setBusy(false);
      setConfirming(null);
    }
  };

  const problems = outcome ? [...list(outcome.refused), ...list(outcome.failed)] : [];
  const queuedCount = outcome ? list(outcome.queued).length : 0;

  return (
    <>
      <div className="v2-scrim" onClick={onClose} role="presentation" />
      <aside className="v2-blade" role="dialog" aria-label="Software">
        <header>
          <div>
            <h2>Add software</h2>
            <div className="v2-sub" title={names}>{`On ${plural(total, 'machine', 'machines')}: ${names}`}</div>
          </div>
          <button type="button" className="v2-iconbtn" onClick={onClose} aria-label="Close">&times;</button>
        </header>
        <div className="body">
          <input className="v2-input" type="search" aria-label="Search software" placeholder="Search all software by name or keyword, for example storage explorer" value={text} onChange={(e) => setText(e.target.value)} />
          {outcome?.error && <div className="v2-alert err" role="alert">{outcome.error}</div>}
          {outcome && !outcome.error && (
            <div className={`v2-alert${problems.length ? ' info' : ''}`} role="status">
              {`${plural(queuedCount, 'install job', 'install jobs')} of ${outcome.name} in the queue.`}
              {list(outcome.queued).map((q) => <div className="v2-sub" key={q.id}>{`${q.name}: ${q.id}`}</div>)}
              {problems.map((p) => <div className="v2-sub" key={`p${p.key}${p.error}`}>{`Not queued, ${p.name}: ${p.error}`}</div>)}
            </div>
          )}
          {found.state === 'loading' && <div><span className="v2-spin" role="status" aria-label="Loading" /></div>}
          {found.state === 'error' && <div className="v2-alert err">{found.error}</div>}
          {found.state === 'idle' && <div className="v2-hint">Type at least two characters to search.</div>}
          {packages.length > 0 && (
            <table className="v2-table" aria-label="Software packages">
              <thead><tr><th>Software</th><th>Newest</th><th /></tr></thead>
              <tbody>
                {packages.map((pkg) => {
                  const have = installedProgram(installed, pkg.id);
                  return (
                    <tr key={pkg.id}>
                      <td className="name" style={{ fontWeight: 600 }}>
                        {pkg.name}
                        <div className="v2-sub">{pkg.id}</div>
                        {have && <div><span className="v2-pill ok" data-testid="already-installed">{`Already installed${have.version ? ` (version ${have.version})` : ''}`}</span></div>}
                      </td>
                      <td>{pkg.latestVersion}</td>
                      <td className="nowrap">
                        <button type="button" className="v2-btn primary" disabled={busy || Boolean(have)} title={have ? 'It is already installed on this machine.' : undefined} aria-label={`Install ${pkg.name}`}
                          onClick={() => setConfirming({ id: pkg.id, name: pkg.name })}>Install</button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          {found.state === 'ok' && packages.length === 0 && <div className="v2-empty">No software matches.</div>}
        </div>
      </aside>

      {confirming && (
        <div className="v2-dialog-host">
          <div className="v2-dialog" role="dialog" aria-label={`Install ${confirming.name}`}>
            <h2>{`Install ${confirming.name}?`}</h2>
            <p>{`It will be installed for real on ${plural(total, 'machine', 'machines')}: ${names}.`}</p>
            <div className="actions">
              <button type="button" className="v2-btn" onClick={() => setConfirming(null)} disabled={busy}>Cancel</button>
              <button type="button" className="v2-btn primary" onClick={() => run(confirming)} disabled={busy}>Install</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
